import type { ExtendedMedia } from '@seen/core/models/Media'
import { Button } from '@seen/ui'
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react'
import { type JSX, useCallback, useRef } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useStableAttachmentUrl } from '#src/hooks/useStableAttachmentUrl.ts'

interface FullScreenGalleryProps {
  media: ExtendedMedia[]
  currentIndex: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onNavigate: (index: number) => void
}

export function FullScreenGallery({
  media,
  currentIndex,
  open,
  onOpenChange,
  onNavigate,
}: FullScreenGalleryProps): JSX.Element {
  const currentMedia = media[currentIndex]
  const stableUrl = useStableAttachmentUrl(currentMedia?.contentUrl)
  const patientAssociation = currentMedia?.patientAssociation
  const onNavigateRef = useRef(onNavigate)
  onNavigateRef.current = onNavigate

  const goToPrevious = useCallback((): void => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : media.length - 1
    onNavigateRef.current(newIndex)
  }, [currentIndex, media.length])

  const goToNext = useCallback((): void => {
    const newIndex = currentIndex < media.length - 1 ? currentIndex + 1 : 0
    onNavigateRef.current(newIndex)
  }, [currentIndex, media.length])

  const handleClose = useCallback((): void => {
    onOpenChange(false)
  }, [onOpenChange])

  useHotkeys('ArrowLeft', goToPrevious)
  useHotkeys('ArrowRight', goToNext)
  useHotkeys('Escape', handleClose)

  if (!currentMedia || !stableUrl) {
    return <></>
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogDisplay
        className="fixed inset-0 z-10000 m-0! mt-0! h-screen! max-h-none! w-screen! max-w-none! rounded-none border-0 bg-black/95 p-0!"
        hideCloseButton
        notScrollable
      >
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
          >
            <IconX className="h-6 w-6" />
          </Button>
        </DialogClose>

        {media.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1/2 left-4 z-10 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={goToPrevious}
            >
              <IconChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-1/2 right-4 z-10 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={goToNext}
            >
              <IconChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}

        <div className="flex h-full w-full flex-col items-center justify-between p-4">
          <div className="flex w-full flex-1 items-center justify-center">
            <div className="relative h-full w-full">
              {currentMedia.contentType === 'application/pdf' ? (
                <iframe
                  src={`${stableUrl}#navpanes=0`}
                  className="h-full w-full"
                  allowFullScreen
                  title={currentMedia.title ?? 'PDF Document'}
                />
              ) : (
                <img
                  src={stableUrl}
                  alt={currentMedia.title ?? 'Memory image'}
                  className="mx-auto max-h-[70vh] max-w-full object-contain"
                />
              )}

              {patientAssociation && (
                <div className="absolute right-0 bottom-0 left-0 bg-black/70 p-4 text-white">
                  <div className="text-sm">
                    <div className="flex justify-between">
                      <span>Confidence:</span>
                      <span className="font-medium">
                        {patientAssociation.similarity.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {currentMedia.contentType !== 'application/pdf' && (
            <div className="w-full pb-4 text-center text-white">
              <div className="text-lg font-medium">
                {currentMedia.title ?? 'Untitled'}
              </div>
              <div className="text-sm text-white/70">
                {currentIndex + 1} of {media.length}
                {media.length > 1 && (
                  <span className="ml-2 text-xs">
                    (Use ← → arrow keys or click buttons to navigate)
                  </span>
                )}
              </div>
              {currentMedia.createdDateTime && (
                <div className="text-sm text-white/70">
                  {new Date(currentMedia.createdDateTime).toLocaleDateString()}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogDisplay>
    </Dialog>
  )
}
